import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { PopulationRecord } from './usePopulationData';
import { Place } from '@/types';

interface PeerPopulationData {
  [key: string]: PopulationRecord[]; // key is place_fips
}

export function usePeerPopulationData(peers: Place[]) {
  const [data, setData] = useState<PeerPopulationData>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!peers.length) {
      setLoading(false);
      setData({});
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    // Create an array of promises for each peer
    const fetchPromises = peers.map(peer => 
      supabase
        .from('ACS_BPS_metrics_demo')
        .select('Place, place_fips, year, total_population')
        .eq('Place', peer.name)
        .eq('place_fips', peer.place_fips)
        .order('year', { ascending: false })
        .limit(2) // Get last 2 years to calculate growth
    );

    Promise.all(fetchPromises)
      .then(results => {
        const newData: PeerPopulationData = {};
        let loggedAny = false;
        results.forEach((result, index) => {
          if (result.error) {
            console.error(`Error fetching data for peer ${peers[index].name}:`, result.error);
          } else {
            const peerData = result.data || [];
            if (peerData.length >= 2) {
              const currentYear = peerData[0];
              const prevYear = peerData[1];
              
              // Calculate growth rate
              const currentPop = Number(currentYear.total_population ?? 0);
              const prevPop = Number(prevYear.total_population ?? 0);
              
              let growth: number | null = null;
              if (prevPop && prevPop !== 0) {
                growth = ((currentPop - prevPop) / prevPop) * 100;
              }

              // Add growth rate to the current year's data
              (currentYear as any).population_growth = growth;
              
              loggedAny = true;
              console.log(`Peer City: ${peers[index].name}`);
              console.log(`  FIPS: ${peers[index].place_fips}`);
              console.log(`  Current Population: ${currentPop.toLocaleString()}`);
              console.log(`  Previous Population: ${prevPop.toLocaleString()}`);
              console.log(`  Growth Rate: ${growth?.toFixed(2)}%`);
              console.log(`  Year: ${currentYear.year}`);
            }
            newData[peers[index].place_fips] = peerData as PopulationRecord[];
          }
        });
        if (loggedAny) {
          console.log('------------------------');
        }
        setData(newData);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching peer population data:', err);
        setError('Failed to fetch peer data');
        setLoading(false);
      });
  }, [peers]);

  return { data, loading, error };
} 